/**
 * Format currency in Indian Rupees (INR)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 * @example formatCurrency(33200000) => "₹3,32,00,000"
 */
export const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '';

    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * Format date to DD-MM-YYYY format
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 * @example formatDate('2025-12-16') => "16-12-2025"
 * @example formatDate('16-12-2025') => "16-12-2025"
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';

    // If date is already in DD-MM-YYYY format, return as is
    if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        return dateString;
    }

    // If date is in YYYY-MM-DD format, convert to DD-MM-YYYY
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
    }

    // Try to parse as a Date object and format
    try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        }
    } catch (error) {
        console.error('Error formatting date:', error);
    }

    return dateString; // Return original if parsing fails
};

/**
 * Parse DD-MM-YYYY date string to Date object
 * @param {string} dateString - Date string in DD-MM-YYYY format
 * @returns {Date|null} Date object or null if invalid
 * @example parseDate('16-12-2025') => Date object
 */
export const parseDate = (dateString) => {
    if (!dateString) return null;

    try {
        // Handle DD-MM-YYYY format
        if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
            const [day, month, year] = dateString.split('-');
            return new Date(year, month - 1, day);
        }

        // Try parsing as is
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? null : date;
    } catch (error) {
        console.error('Error parsing date:', error);
        return null;
    }
};

/**
 * Format EMD submission date to readable format with time
 * @param {string|Date} dateString - The date to format (with or without time)
 * @returns {string} Formatted date string like "6th January 2026 before 4.00 pm"
 * @example formatEMDDate('2026-01-06T16:00:00') => "6th January 2026 before 4.00 pm"
 * @example formatEMDDate('2026-01-06 16:00:00') => "6th January 2026 before 4.00 pm"
 */
export const formatEMDDate = (dateString) => {
    if (!dateString) return '';

    try {
        let date;

        // Handle DD-MM-YYYY format (date only)
        if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
            const [day, month, year] = dateString.split('-');
            date = new Date(year, month - 1, day);
        }
        // Handle YYYY-MM-DD format (date only)
        else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            date = new Date(dateString);
        }
        // Handle datetime formats (YYYY-MM-DD HH:MM:SS or ISO format)
        else {
            date = new Date(dateString);
        }

        if (isNaN(date.getTime())) return dateString;

        // Get day with ordinal suffix
        const day = date.getDate();
        const ordinalSuffix = (day) => {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        // Get month name
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        // Extract time from the date
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12; // Convert 0 to 12
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const timeString = `${hours}.${formattedMinutes} ${ampm}`;

        return `${day}${ordinalSuffix(day)} ${month} ${year} before ${timeString}`;
    } catch (error) {
        console.error('Error formatting EMD date:', error);
        return dateString;
    }
};

/**
 * Format number to Indian numbering system (Lakhs/Crores)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted number string
 * @example formatIndianNumber(3320000) => "33.2 Lakhs"
 * @example formatIndianNumber(50000000) => "5 Crores"
 */
export const formatIndianNumber = (amount) => {
    if (!amount && amount !== 0) return '';

    const absAmount = Math.abs(amount);

    if (absAmount >= 10000000) { // Crore
        const crores = (amount / 10000000).toFixed(2);
        return `${crores} Cr`;
    } else if (absAmount >= 100000) { // Lakh
        const lakhs = (amount / 100000).toFixed(2);
        return `${lakhs} L`;
    } else if (absAmount >= 1000) { // Thousand
        const thousands = (amount / 1000).toFixed(2);
        return `${thousands} K`;
    }

    return amount.toString();
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 * @example truncateText('Long text here', 10) => "Long text..."
 */
export const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
};

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Check if value is empty (null, undefined, empty string, empty array)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
};

/**
 * Sort array of objects by a key
 * @param {Array} array - Array to sort
 * @param {string} key - Key to sort by
 * @param {string} order - 'asc' or 'desc'
 * @returns {Array} Sorted array
 */
export const sortByKey = (array, key, order = 'asc') => {
    return [...array].sort((a, b) => {
        const aVal = a[key];
        const bVal = b[key];

        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
    });
};
